/* AuraSkin — product page behaviour (gallery, variants, cart, routine, reviews) */
let CartEvt = null;
try {
  ({ CartLinesUpdateEvent: CartEvt } = await import('@shopify/events'));
} catch (_) { CartEvt = null; }

const cartRoutes = () => (window.Theme && window.Theme.routes) || {};

export async function auraCartAdd(items, source) {
  items = items.filter((i) => i.id);
  if (!items.length) return null;
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId).filter(Boolean).join(',');
  let deferred = null;
  if (CartEvt) {
    try {
      deferred = CartEvt.createPromise();
      (source || document).dispatchEvent(new CartEvt({
        action: 'add', context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      }));
    } catch (_) { deferred = null; }
  }
  const addUrl = (cartRoutes().cart_add_url || '/cart/add') + '.js';
  const res = await fetch(addUrl.replace('.js.js', '.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: items.map((i) => ({ id: Number(i.id), quantity: i.quantity })), sections: sectionIds }),
  });
  const data = await res.json();
  if (data.status) {
    if (deferred) deferred.reject(new Error(data.description || data.message));
    throw new Error(data.description || data.message || 'Could not add to cart');
  }
  const cart = await fetch((cartRoutes().cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  if (deferred) {
    deferred.resolve({
      cart: CartEvt.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'aura-pdp', sourceId: 'aura-pdp', itemCount: items.reduce((s, i) => s + i.quantity, 0), sections: data.sections, didError: false },
    });
  } else {
    window.location.href = cartRoutes().cart_url || '/cart';
  }
  return cart;
}

export function auraMoney(cents, format) {
  format = format || window.auraMoneyFormat || '{{amount}}';
  const fmt = (n, dec, t = ',', d = '.') => {
    const parts = (n / 100).toFixed(dec).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
    return parts.join(d);
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return fmt(cents, 0);
      case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
      case 'amount_with_period_and_space_separator': return fmt(cents, 2, ' ', '.');
      default: return fmt(cents, 2);
    }
  });
}

async function withLoading(btn, fn) {
  btn.classList.add('is-loading');
  const label = btn.innerHTML;
  try {
    await fn();
    btn.innerHTML = btn.dataset.added || 'Added';
    setTimeout(() => (btn.innerHTML = label), 1400);
  } catch (e) {
    btn.innerHTML = label;
    throw e;
  } finally {
    btn.classList.remove('is-loading');
  }
}

/* ---------- Main product section ---------- */
function initMain(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = '1';

  // Gallery
  const track = root.querySelector('.aura-gal__track');
  const slides = track ? [...track.children] : [];
  const thumbs = [...root.querySelectorAll('.aura-gal__thumb')];
  const counter = root.querySelector('[data-gal-count]');
  let current = 0;
  const setActive = (i) => {
    current = i;
    thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
    if (counter) counter.textContent = i + 1;
    const t = thumbs[i];
    if (t && t.parentElement.scrollWidth > t.parentElement.clientWidth) {
      t.parentElement.scrollTo({ left: t.offsetLeft - 16, behavior: 'smooth' });
    }
  };
  const goTo = (i) => {
    if (!track || !slides[i]) return;
    track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
    setActive(i);
  };
  thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));
  root.querySelector('.aura-gal__nav--prev')?.addEventListener('click', () => goTo(Math.max(0, current - 1)));
  root.querySelector('.aura-gal__nav--next')?.addEventListener('click', () => goTo(Math.min(slides.length - 1, current + 1)));
  if (track) {
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== current) setActive(i);
      });
    }, { passive: true });
  }

  // Variants
  const variants = JSON.parse(root.querySelector('[data-aura-variants]')?.textContent || '[]');
  const idInput = root.querySelector('input[name="id"]');
  const priceNow = root.querySelector('[data-price-now]');
  const priceWas = root.querySelector('[data-price-was]');
  const priceSave = root.querySelector('[data-price-save]');
  const addBtn = root.querySelector('[data-add]');
  const buyBtn = root.querySelector('[data-buy]');
  const groups = [...root.querySelectorAll('[data-opt]')];
  const selected = groups.map((g) => g.querySelector('.is-active')?.dataset.value);

  const findVariant = () => variants.find((v) => v.options.every((o, i) => o === selected[i]));
  const refreshAvailability = () => {
    groups.forEach((g, gi) => {
      g.querySelectorAll('.aura-opt__val').forEach((b) => {
        const test = [...selected]; test[gi] = b.dataset.value;
        const ok = variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
        b.classList.toggle('is-na', !ok);
      });
    });
  };
  const applyVariant = () => {
    const v = findVariant();
    if (idInput) idInput.value = v ? v.id : '';
    if (v) {
      if (priceNow) priceNow.innerHTML = v.price;
      if (priceWas) { priceWas.innerHTML = v.compare || ''; priceWas.hidden = !v.compare; }
      if (priceSave) { priceSave.textContent = v.save || ''; priceSave.hidden = !v.save; }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      if (v.media) {
        const idx = slides.findIndex((s) => s.dataset.mediaId === String(v.media));
        if (idx > -1) goTo(idx);
      }
    }
    const ok = !!(v && v.available);
    [addBtn, buyBtn].forEach((b) => { if (b) b.disabled = !ok; });
    if (addBtn) addBtn.textContent = !v ? addBtn.dataset.unavailable : ok ? addBtn.dataset.label : addBtn.dataset.soldout;
    refreshAvailability();
  };
  groups.forEach((g, gi) => {
    g.addEventListener('click', (e) => {
      const b = e.target.closest('.aura-opt__val');
      if (!b) return;
      g.querySelectorAll('.aura-opt__val').forEach((x) => { x.classList.remove('is-active'); x.setAttribute('aria-pressed', 'false'); });
      b.classList.add('is-active'); b.setAttribute('aria-pressed', 'true');
      selected[gi] = b.dataset.value;
      const lbl = root.querySelector(`[data-opt-selected="${gi}"]`);
      if (lbl) lbl.textContent = b.dataset.value;
      applyVariant();
    });
  });
  if (groups.length) refreshAvailability();

  // Quantity
  const qty = root.querySelector('.aura-qty input');
  root.querySelectorAll('[data-qty]').forEach((b) => b.addEventListener('click', () => {
    qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + Number(b.dataset.qty));
  }));

  // Cart
  const msg = root.querySelector('.aura-msg');
  const showErr = (e) => { if (msg) { msg.textContent = e.message; setTimeout(() => (msg.textContent = ''), 6000); } };
  const items = () => [{ id: idInput.value, quantity: Math.max(1, parseInt(qty?.value, 10) || 1) }];
  addBtn?.addEventListener('click', () => withLoading(addBtn, () => auraCartAdd(items(), root)).catch(showErr));
  buyBtn?.addEventListener('click', async () => {
    try {
      buyBtn.classList.add('is-loading');
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items() }) });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      window.location.href = '/checkout';
    } catch (e) { buyBtn.classList.remove('is-loading'); showErr(e); }
  });

  // Description clamp
  const firstBody = root.querySelector('.aura-acc details .aura-acc__body');
  if (firstBody && !firstBody.querySelector('.aura-ticks') && !firstBody.querySelector('[data-clamp]')) {
    const wrap = document.createElement('div');
    wrap.className = 'aura-clamp'; wrap.dataset.clamp = '';
    while (firstBody.firstChild) wrap.appendChild(firstBody.firstChild);
    const btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'aura-more'; btn.dataset.more = ''; btn.hidden = true; btn.textContent = root.dataset.tMore || 'Read more';
    firstBody.append(wrap, btn);
  }
  const clamp = root.querySelector('[data-clamp]');
  const more = root.querySelector('[data-more]');
  if (clamp && more) {
    const check = () => {
      const long = clamp.scrollHeight > clamp.clientHeight + 8;
      clamp.classList.toggle('is-short', !long);
      more.hidden = !long && !clamp.classList.contains('is-open');
    };
    root.querySelector('.aura-acc details')?.addEventListener('toggle', check);
    check();
    more.addEventListener('click', () => {
      const open = clamp.classList.toggle('is-open');
      more.textContent = open ? (root.dataset.tLess || 'Read less') : (root.dataset.tMore || 'Read more');
    });
  }

  // Wishlist (per-browser)
  const wish = root.querySelector('.aura-wish');
  if (wish) {
    const key = 'aura-wishlist';
    const read = () => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (_) { return []; } };
    const id = wish.dataset.handle;
    const paint = () => { const on = read().includes(id); wish.classList.toggle('is-on', on); wish.setAttribute('aria-pressed', on); };
    paint();
    wish.addEventListener('click', () => {
      let list = read();
      list = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      try { localStorage.setItem(key, JSON.stringify(list)); } catch (_) {}
      paint();
    });
  }
}

/* ---------- Routine section ---------- */
async function initRoutine(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = '1';
  const grid = root.querySelector('.aura-rt__grid');
  const url = root.dataset.recsUrl;

  if (url && grid) {
    const load = async (intent) => {
      const r = await fetch(`${url}&intent=${intent}`);
      const html = new DOMParser().parseFromString(await r.text(), 'text/html');
      return [...html.querySelectorAll('.aura-rt__card')];
    };
    try {
      let cards = await load('complementary');
      if (cards.length < 2) {
        const related = await load('related');
        const seen = new Set(cards.map((c) => c.dataset.productId));
        cards = cards.concat(related.filter((c) => !seen.has(c.dataset.productId)));
      }
      const max = Number(root.dataset.max || 4);
      const currentId = grid.querySelector('.is-current')?.dataset.productId;
      cards.filter((c) => c.dataset.productId !== currentId).slice(0, max).forEach((c) => grid.appendChild(document.importNode(c, true)));
    } catch (_) {}
    if (grid.children.length < 2) { root.hidden = true; return; }
    // Re-number steps
    [...grid.children].forEach((c, i) => {
      const s = c.querySelector('[data-step-n]');
      if (s) s.textContent = i + 1;
    });
  }

  const cards = () => [...grid.querySelectorAll('.aura-rt__card')].filter((c) => c.dataset.variantId && c.dataset.available === 'true');
  const total = root.querySelector('[data-kit-total]');
  const count = root.querySelector('[data-kit-count]');
  const list = cards();
  if (total) total.textContent = auraMoney(list.reduce((s, c) => s + Number(c.dataset.price || 0), 0));
  if (count) count.textContent = list.length;
  if (list.length < 2) root.querySelector('.aura-rt__kit')?.setAttribute('hidden', '');

  root.addEventListener('click', (e) => {
    const one = e.target.closest('[data-rt-add]');
    if (one) {
      const card = one.closest('.aura-rt__card');
      withLoading(one, () => auraCartAdd([{ id: card.dataset.variantId, quantity: 1 }], root)).catch(() => {});
    }
    const all = e.target.closest('[data-rt-all]');
    if (all) withLoading(all, () => auraCartAdd(cards().map((c) => ({ id: c.dataset.variantId, quantity: 1 })), root)).catch(() => {});
  });
}

/* ---------- Reviews section ---------- */
function initReviews(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = '1';
  const btns = [...root.querySelectorAll('.aura-rv__filters button')];
  btns.forEach((b) => b.addEventListener('click', () => {
    btns.forEach((x) => x.classList.toggle('is-active', x === b));
    const f = b.dataset.filter;
    root.querySelectorAll('.aura-rv__card').forEach((c) => { c.hidden = f !== 'all' && c.dataset.skin !== f; });
  }));
}

function boot(scope = document) {
  scope.querySelectorAll('[data-aura-pdp]').forEach(initMain);
  scope.querySelectorAll('[data-aura-routine]').forEach(initRoutine);
  scope.querySelectorAll('[data-aura-reviews]').forEach(initReviews);
}
boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
